import React, { useEffect, useState } from "react";
import FolderTabLayout from "./ui/folder-tab-layout";
import { AlertTriangle, FileWarning, Landmark, Car, Upload, History } from "lucide-react";
import InsuranceDriverHistoryTab from "./InsuranceDriverHistoryTab";
import InsuranceIncidentsTab from "./InsuranceIncidentsTab";
import InsuranceClaimsTab from "./InsuranceClaimsTab";
import InsuranceExcessFundTab from "./InsuranceExcessFundTab";
import InsuranceExcessReconciliationTab from "./InsuranceExcessReconciliationTab";
import InsuranceVehicleValuesTab from "./InsuranceVehicleValuesTab";
import { consumeNavPath } from "@/lib/adminNav";
import { useAppContext } from "@/contexts/AppContext";

const InsuranceHub: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) never sees Bank Reconciliation -- it's a whole-bank-statement
  // matching workflow across every driver, not something scoped to one
  // driver/vehicle, same treatment as Fleet Pulse/Fleet Capacity/IVR Calls.
  const { effectivePermissions } = useAppContext();
  const isUnrestricted = effectivePermissions.isUnrestricted;

  const [tab, setTab] = useState("incidents");
  // Lets the Claims tab's "Edit Incident / Diagram" button jump straight to
  // that incident's detail view instead of leaving Janelle to find it again
  // in the Incidents list -- consumed (cleared) once InsuranceIncidentsTab
  // has opened it, so switching tabs again doesn't re-open it.
  const [openIncidentId, setOpenIncidentId] = useState<string | null>(null);

  // Deep-link support for the Site Map (e.g. Insurance -> Excess Fund) and for
  // Closed Check's "Review" button on an unreviewed incident, which passes
  // the specific incident id as path[1] to jump straight to it.
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[0]) setTab(path[0]);
    if (path?.[1]) setOpenIncidentId(path[1]);
  }, []);

  // Redirect away if a deep-link or stale state ever lands a restricted
  // user on the hidden Bank Reconciliation tab directly.
  useEffect(() => {
    if (!isUnrestricted && tab === "reconciliation") setTab("incidents");
  }, [isUnrestricted, tab]);

  return (
    <FolderTabLayout
      tabs={[
        { value: "incidents", label: "Incidents", icon: AlertTriangle },
        { value: "claims", label: "Claims", icon: FileWarning },
        { value: "driverhistory", label: "Driver History", icon: History },
        { value: "excessfund", label: "Excess Fund", icon: Landmark },
        ...(isUnrestricted ? [{ value: "reconciliation", label: "Bank Reconciliation", icon: Upload }] : []),
        { value: "vehiclevalues", label: "Vehicle Values", icon: Car },
      ]}
      value={tab}
      onValueChange={setTab}
    >
      {tab === "incidents" && (
        <InsuranceIncidentsTab initialOpenIncidentId={openIncidentId} onInitialOpenConsumed={() => setOpenIncidentId(null)} />
      )}
      {tab === "claims" && (
        <InsuranceClaimsTab
          onEditIncident={(incidentId) => {
            setOpenIncidentId(incidentId);
            setTab("incidents");
          }}
        />
      )}
      {tab === "driverhistory" && (
        <InsuranceDriverHistoryTab
          onOpenIncident={(incidentId) => {
            setOpenIncidentId(incidentId);
            setTab("incidents");
          }}
        />
      )}
      {tab === "excessfund" && <InsuranceExcessFundTab />}
      {tab === "reconciliation" && isUnrestricted && <InsuranceExcessReconciliationTab />}
      {tab === "vehiclevalues" && <InsuranceVehicleValuesTab />}
    </FolderTabLayout>
  );
};

export default InsuranceHub;
